import React, { useMemo, useState } from "react";
import { formatAmount, formatCurrencyTotal, formatDate, formatDateTime } from "./budgetTrackUtils";

const REVIEW_FILTERS = [
  { id: "all", label: "All" },
  { id: "needs_review", label: "Needs review" },
  { id: "needs_amount", label: "Needs amount" },
  { id: "ready", label: "Ready" },
  { id: "linked", label: "Linked" },
];

function tagClass(status) {
  if (status === "needs_review" || status === "needs_amount") return "review";
  if (status === "ready" || status === "linked") return "ok";
  return "skip";
}

export function BudgetTrackStats({ overview }) {
  const totals = overview?.totals || {};
  const amountByCurrency = totals.amount_by_currency || {};
  const spend =
    Object.keys(amountByCurrency).length > 0
      ? Object.entries(amountByCurrency)
          .slice(0, 2)
          .map(([c, a]) => formatCurrencyTotal(c, a))
          .join(" + ")
      : "0";

  return (
    <div className="btx-stats">
      <div className="btx-stats__item">
        <span className="btx-stats__num">{totals.pdf_count ?? 0}</span>
        <span className="btx-stats__lbl">PDF invoices</span>
      </div>
      <div className={`btx-stats__item ${totals.missing_pdf_count > 0 ? "btx-stats__item--alert" : ""}`}>
        <span className="btx-stats__num">{totals.missing_pdf_count ?? 0}</span>
        <span className="btx-stats__lbl">Missing PDF</span>
      </div>
      <div className="btx-stats__item">
        <span className="btx-stats__num">{totals.needs_review_count ?? 0}</span>
        <span className="btx-stats__lbl">Needs review</span>
      </div>
      <div className="btx-stats__item">
        <span className="btx-stats__num" style={{ fontSize: "1rem" }}>
          {spend}
        </span>
        <span className="btx-stats__lbl">Spend</span>
      </div>
    </div>
  );
}

export function BudgetTrackInbox({
  emails,
  loading,
  selectedId,
  onSelect,
  reviewFilter,
  onReviewFilterChange,
  search,
  onSearchChange,
  creatingExpenseId,
}) {
  const rows = emails?.data || emails || [];

  const filtered = useMemo(() => {
    let list = rows;
    if (reviewFilter !== "all") {
      list = list.filter((e) => e.review_status === reviewFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          (e.subject || "").toLowerCase().includes(q) ||
          (e.sender_email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, reviewFilter, search]);

  return (
    <div className="btx-inbox">
      <div className="btx-inbox__head">
        <div className="btx-inbox__chips">
          {REVIEW_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`btx-inbox__chip ${reviewFilter === f.id ? "active" : ""}`}
              onClick={() => onReviewFilterChange(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="btx-inbox__search"
          placeholder="Search subject or sender…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="btx-inbox__list">
        {loading ? (
          <div className="btx-inbox__empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="btx-inbox__empty">No emails match.</div>
        ) : (
          filtered.map((email) => {
            const needsAttention =
              email.review_status === "needs_review" || email.review_status === "needs_amount";
            const isSelected = selectedId === email.id;
            const isWorking = creatingExpenseId === email.id;

            return (
              <button
                key={email.id}
                type="button"
                className={[
                  "btx-inbox__row",
                  isSelected ? "selected" : "",
                  !isSelected && needsAttention ? "attention" : "",
                  isWorking ? "working" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => onSelect(email)}
              >
                <div className="btx-inbox__subject">{email.subject || "(no subject)"}</div>
                <div className="btx-inbox__sub">{email.sender_email}</div>
                <div className="btx-inbox__row-foot">
                  <span className={`btx-inbox__tag ${tagClass(email.review_status)}`}>
                    {(email.review_status || "unknown").replace("_", " ")}
                  </span>
                  <span className="btx-inbox__amount">{formatAmount(email.manual_amount, email.currency)}</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export function BudgetTrackDetailPane({
  email,
  overview,
  year,
  onYearChange,
  vendors,
  runs,
  syncing,
  currencies,
  saving,
  creating,
  onSave,
  onCreateExpense,
}) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [notes, setNotes] = useState("");

  React.useEffect(() => {
    if (email) {
      setAmount(email.manual_amount ?? "");
      setCurrency(email.currency || "EUR");
      setNotes(email.notes || "");
    }
  }, [email]);

  if (!email) {
    const months = overview?.months || [];
    const maxV = Math.max(...(vendors || []).map((v) => Number(v.manual_amount_total || 0)), 1);

    return (
      <div className="btx-detail">
        <div className="btx-detail__welcome">
          <p className="btx-detail__welcome-title">
            Dashboard
            <input
              type="number"
              className="form-control form-control-sm d-inline-block ms-2"
              style={{ width: 72, verticalAlign: "middle" }}
              value={year}
              onChange={(e) => onYearChange(Number(e.target.value))}
            />
          </p>

          <div className="row g-3">
            <div className="col-md-7">
              <h6 className="small fw-bold text-muted mb-2">MONTHLY</h6>
              {months.length > 0 ? (
                <table className="btx-mini-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Emails</th>
                      <th>Missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m) => (
                      <tr key={m.month}>
                        <td>{m.month}</td>
                        <td>{m.email_count ?? 0}</td>
                        <td className={m.missing_pdf_count > 0 ? "text-danger" : ""}>
                          {m.missing_pdf_count ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-muted small">No monthly data.</p>
              )}
            </div>
            <div className="col-md-5">
              <h6 className="small fw-bold text-muted mb-2">TOP VENDORS</h6>
              {(vendors || []).slice(0, 5).map((v) => {
                const amt = Number(v.manual_amount_total || 0);
                return (
                  <div key={v.id || v.vendor_key} className="btx-vendor-line">
                    <span className="btx-vendor-line__name">{v.vendor_name || v.vendor_key}</span>
                    <div className="btx-vendor-line__bar">
                      <div className="btx-vendor-line__fill" style={{ width: `${(amt / maxV) * 100}%` }} />
                    </div>
                    <span className="btx-vendor-line__amt">{amt.toFixed(0)}</span>
                  </div>
                );
              })}
              {!vendors?.length && <p className="text-muted small">No vendors yet.</p>}
            </div>
          </div>

          {runs?.length > 0 && (
            <div className="mt-3">
              <h6 className="small fw-bold text-muted mb-2">
                LAST SYNC {syncing && <span className="text-warning">(running)</span>}
              </h6>
              <p className="small mb-0 text-muted">
                {formatDateTime(runs[0].started_at)} — {runs[0].status}, {runs[0].messages_new ?? 0} new
              </p>
            </div>
          )}

          <p className="text-muted small mt-4 mb-0">← Select an email from the list to view and edit details.</p>
        </div>
      </div>
    );
  }

  const amountStep = currency === "TND" ? "0.001" : "0.01";

  return (
    <div className="btx-detail">
      <div className="btx-detail__body">
        <div className="btx-detail__header">
          <h2 className="btx-detail__title">{email.subject || "(no subject)"}</h2>
          <p className="btx-detail__from">
            From {email.sender_email} · {formatDate(email.received_at)}
          </p>
        </div>

        <div className="btx-detail__meta-row">
          <span className={`btx-detail__badge ${email.pdf_count > 0 ? "green" : "red"}`}>
            {email.pdf_count > 0 ? `${email.pdf_count} PDF` : "No PDF"}
          </span>
          <span className="btx-detail__badge">{email.document_type || "other"}</span>
          <span className={`btx-detail__badge ${email.expense_id ? "green" : "amber"}`}>
            {email.expense_id ? `Expense #${email.expense_id}` : "Not linked"}
          </span>
        </div>

        {email.body_excerpt && <div className="btx-detail__excerpt">{email.body_excerpt}</div>}

        <div className="btx-detail__grid">
          <div className="btx-detail__field">
            <label>Amount</label>
            <input type="number" step={amountStep} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="btx-detail__field">
            <label>Currency</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {(currencies || ["EUR", "USD", "GBP", "TND"]).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="btx-detail__field" style={{ gridColumn: "1 / -1" }}>
            <label>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="btx-detail__actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={saving}
            onClick={() => onSave({ manual_amount: amount || null, currency, notes })}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          {!email.expense_id && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={creating}
              onClick={() => onCreateExpense(email)}
            >
              {creating ? "Creating…" : "Create expense"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function BudgetTrackSettingsDrawer({ open, onClose, rules, saving, onSave, runs, syncing }) {
  const [keywords, setKeywords] = useState("");
  const [allowlist, setAllowlist] = useState("");
  const [denylist, setDenylist] = useState("");

  React.useEffect(() => {
    if (rules) {
      setKeywords((rules.keywords || []).join(", "));
      setAllowlist((rules.sender_allowlist || []).join(", "));
      setDenylist((rules.sender_denylist || []).join(", "));
    }
  }, [rules]);

  if (!open) return null;

  const split = (v) =>
    v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <>
      <div className="btx-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="btx-drawer" role="dialog" aria-label="Settings">
        <div className="btx-drawer__head">
          <span>Settings</span>
          <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="btx-drawer__body">
          <div className="btx-drawer__section">
            <h6>Scan keywords</h6>
            <textarea value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="invoice, receipt…" />
          </div>
          <div className="btx-drawer__section">
            <h6>Sender allowlist</h6>
            <textarea value={allowlist} onChange={(e) => setAllowlist(e.target.value)} />
          </div>
          <div className="btx-drawer__section">
            <h6>Sender denylist</h6>
            <textarea value={denylist} onChange={(e) => setDenylist(e.target.value)} />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm w-100 mb-4"
            disabled={saving}
            onClick={() =>
              onSave({
                keywords: split(keywords),
                sender_allowlist: split(allowlist),
                sender_denylist: split(denylist),
              })
            }
          >
            {saving ? "Saving…" : "Save scan rules"}
          </button>

          <div className="btx-drawer__section">
            <h6>Sync history {syncing && <span className="text-warning">●</span>}</h6>
            {(runs || []).slice(0, 8).map((run) => (
              <div key={run.id} className="small py-1 border-bottom">
                <strong>{run.status}</strong> · {formatDateTime(run.started_at)}
                <br />
                <span className="text-muted">
                  {run.messages_new ?? 0} new, {run.missing_pdf_new ?? 0} missing PDF
                </span>
              </div>
            ))}
            {!runs?.length && <p className="text-muted small">No sync runs yet.</p>}
          </div>
        </div>
      </aside>
    </>
  );
}
